import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import AuthPage from '../components/layout/AuthPage';

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isCompletingRegistration, setIsCompletingRegistration] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentStep, setCurrentStep] = useState('email');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (resendCountdown > 0) {
      interval = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCountdown]);

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsCheckingEmail(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/register/check-email`, { email });

      if (response.data.needsVerification && response.data.emailSent) {
        setSuccess('A new verification email has been sent to your email address. Please check your inbox and click the link to complete your registration.');
        setCanResend(false);
        setResendCountdown(300);
        return;
      }

      if (response.data.canRegister && response.data.contactInfo) {
        setFirstName(response.data.contactInfo.firstName || '');
        setLastName(response.data.contactInfo.lastName || '');
        setCurrentStep('confirm');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to check email';
      setError(errorMessage);

      if (err.response?.status === 409 && err.response?.data?.suggestion === 'login') {
        setTimeout(() => navigate('/app'), 3000);
      }
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    setError('');
    setIsCompletingRegistration(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/register/complete`, { email, firstName, lastName });
      setSuccess('Registration initiated! Check your email for the verification link.');
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setIsCompletingRegistration(false);
    }
  };

  const handleBackToEmail = () => {
    setCurrentStep('email');
    setFirstName('');
    setLastName('');
    setError('');
  };

  return (
    <AuthPage wide>
      <h1 className="auth-card__title">
        {currentStep === 'email' ? 'Join Garden Sync' : 'Confirm Your Information'}
      </h1>

      {success && <Alert variant="success">{success}</Alert>}

      {currentStep === 'email' ? (
        <Form onSubmit={handleCheckEmail}>
          <Form.Group className="mb-3">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email to get started"
              required
              disabled={isCheckingEmail}
            />
            <Form.Text className="d-block text-end mt-1">
              Please use the same email as your community.gardensync.com forum account.
              We&apos;ll check if you&apos;re in our system and send you a verification link.
            </Form.Text>
          </Form.Group>
          <Button
            type="submit"
            className="button w-100"
            disabled={isCheckingEmail || !canResend}
          >
            {isCheckingEmail ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Checking...
              </>
            ) : !canResend ? (
              `Resend available in ${Math.floor(resendCountdown / 60)}:${(resendCountdown % 60).toString().padStart(2, '0')}`
            ) : (
              'Continue'
            )}
          </Button>
        </Form>
      ) : (
        <Form onSubmit={handleCompleteRegistration}>
          <Alert variant="info" className="mb-3">
            <strong>Email found!</strong> We found your information in our system.
            Please confirm your details below.
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              required
              disabled={isCompletingRegistration}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter your last name"
              required
              disabled={isCompletingRegistration}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" value={email} disabled />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={handleBackToEmail}
              disabled={isCompletingRegistration}
              className="flex-fill"
            >
              Back
            </Button>
            <Button
              type="submit"
              className="button flex-fill"
              disabled={isCompletingRegistration}
            >
              {isCompletingRegistration ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending...
                </>
              ) : (
                'Send Verification Link'
              )}
            </Button>
          </div>
        </Form>
      )}

      <p className="text-center mt-3 mb-0">
        <span className="auth-link" onClick={() => navigate('/app')}>
          Already have an account? Login
        </span>
      </p>
      {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
    </AuthPage>
  );
};

export default RegisterPage;